import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import Login from "./pages/Login";
import Redirect from "./pages/Redirect/Redirect";
import Sidebar from "./pages/SideBar/Sidebar";

const Table = lazy(() => import("./pages/Table/index"));
const GlobalActions = lazy(() => import("./pages/Panel/GlobalActions"));
const Overview = lazy(() => import("./pages/Panel/Overview"));
const Form = lazy(() => import("./pages/Form/Form"));
const Main = lazy(() => import("./pages/Main"));
const Invoice = lazy(() => import("./pages/Invoice/Invoice"));
const SendMailInv = lazy(() => import("./pages/Invoice/SendMailInv"));
const StudentInv = lazy(() => import("./pages/Invoice/StudentInv"));
const Auditing = lazy(() => import("./pages/Auditing/Auditing"));
const StudentAud = lazy(() => import("./pages/Auditing/StudentAud"));
const Edit = lazy(() => import("./pages/Edit"));
const AddUser = lazy(() => import("./pages/AddUser/AddUser"));
const SendMailAud = lazy(() => import("./pages/Auditing/SendMailAud"));
const ScoreHandler = lazy(() => import("./pages/ScoreHandler/ScoreHandler"));
const StudentScore = lazy(() => import("./pages/ScoreHandler/StudentScore"));
const Print = lazy(() => import("./pages/Print/Print"));
const StudentPrint = lazy(() => import("./pages/Print/StudentPrint"));
const Moe = lazy(() => import("./pages/Moe/Moe"));
const StudentMoe = lazy(() => import("./pages/Moe/StudentMoe"));
const Deliver = lazy(() => import("./pages/Deliver/Deliver"));
const StudentDeliver = lazy(() => import("./pages/Deliver/StudentDeliver"));
const Done = lazy(() => import("./pages/Done/Done"));
const StudentDone = lazy(() => import("./pages/Done/StudentDone"));
const ScoreComment = lazy(() => import("./pages/ScoreHandler/ScoreComment"));
const MoeComment = lazy(() => import("./pages/Moe/MoeComment"));
const MOHE = lazy(() => import("./pages/MOHE/MOHE"));
const StudentMOHE = lazy(() => import("./pages/MOHE/StudentMOHE"));
const AudPend = lazy(() => import("./pages/Auditing/AudPend"));
const TransferForm = lazy(() => import("./pages/Transfer/TransferForm"));
const TransferSuccess = lazy(() => import("./pages/Transfer/TransferSuccess"));
const TransferError = lazy(() => import("./pages/Transfer/TransferError"));
const TransferSubmissions = lazy(() => import("./pages/Transfer/TransferSubmissions"));
const Schools = lazy(() => import("./pages/Schools/Schools"));
const UploadEnrollmentLetters = lazy(() => import("./pages/EnrollmentLetters/UploadEnrollmentLetters"));
const QrCodes = lazy(() => import("./pages/QrCodes/QrCodes"));
const TopAchieversGuests = lazy(() => import("./pages/TopAchieversGuests/TopAchieversGuests"));
const TopAchieversInvitation = lazy(() => import("./pages/TopAchieversInvitation/TopAchieversInvitation"));
const TopAchieversPhotos = lazy(() => import("./pages/TopAchieversPhotos/TopAchieversPhotos"));
const AllTopAchievers = lazy(() => import("./pages/AllTopAchievers/AllTopAchievers"));
const Efada = lazy(() => import("./pages/Efada/Efada"));
const EfadaHistory = lazy(() => import("./pages/Efada/EfadaHistory"));
const RushStudents = lazy(() => import("./pages/RushStudents/RushStudents"));
const Reports = lazy(() => import("./pages/Reports/Reports"));
const SendScoreForm = lazy(() => import("./pages/SendScore/SendScoreForm"));
const SendScoreSubmissions = lazy(() => import("./pages/SendScore/SendScoreSubmissions"));
const ReservationForm = lazy(() => import("./pages/Reservation/ReservationForm"));
const ReservationSlots = lazy(() => import("./pages/Reservation/ReservationSlots"));
const ReservationScan = lazy(() => import("./pages/Reservation/ReservationScan"));

// Pages are split into their own files and downloaded when first opened.
const PageLoading = () => (
  <div className="flex items-center justify-center py-24 text-slate-500" role="status">
    Loading…
  </div>
);

function App() {
  return (
    <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route path="/form/*" element={<Form />} />
      <Route path="/send-score" element={<SendScoreForm />} />
      <Route path="/transfer" element={<TransferForm />} />
      <Route path="/transfer/success" element={<TransferSuccess />} />
      <Route path="/transfer/error" element={<TransferError />} />
      <Route path="/reserve" element={<ReservationForm />} />
      <Route path="/redirect" element={<Redirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/main" element={<Sidebar />}>
        <Route path="" element={<Main />} />
        {/* start admin routes */}
        <Route path="overview" element={<Overview />} />
        <Route path="table" element={<Table />} />
        <Route path="actions" element={<GlobalActions />} />
        <Route path="add_user" element={<AddUser />} />
        {/* end admin routes */}
        {/* start auditing routes */}
        <Route path="auditing/new" element={<Auditing />} />
        <Route path="auditing/new/:id" element={<StudentAud />} />
        <Route path="auditing/new/edit" element={<Edit />} />
        <Route path="auditing/request_edit" element={<Auditing />} />
        <Route path="auditing/request_edit/:id" element={<StudentAud />} />
        <Route path="auditing/request_edit/edit" element={<Edit />} />
        <Route path="auditing/request_edit/mail" element={<SendMailAud />} />
        <Route path="auditing/pending" element={<AudPend />} />
        <Route path="auditing/pending/:id" element={<StudentAud />} />
        {/* end auditing routes */}
        {/* start invoice routes */}
        <Route path="invoice/new" element={<Invoice />} />
        <Route path="invoice/another_invoice" element={<Invoice />} />
        <Route path="invoice/wait" element={<Invoice />} />
        <Route path="invoice/new/:id" element={<StudentInv />} />
        <Route path="invoice/another_invoice/:id" element={<StudentInv />} />
        <Route path="invoice/wait/:id" element={<StudentInv />} />
        <Route path="invoice/new/mail" element={<SendMailInv />} />
        {/* end invoice routes */}
        {/* start score request routes */}
        <Route path="score/score_validation" element={<ScoreHandler />} />
        <Route path="score/new" element={<ScoreHandler />} />
        <Route path="score/score_pending" element={<ScoreHandler />} />
        <Route path="score/score_validation/:id" element={<StudentScore />} />
        <Route path="score/new/:id" element={<StudentScore />} />
        <Route path="score/score_pending/:id" element={<StudentScore />} />
        <Route path="score/score_pending/comment" element={<ScoreComment />} />
        {/* end score request routes */}
        {/* start print routes */}
        <Route path="print/new" element={<Print />} />
        <Route path="print/print_validation" element={<Print />} />
        <Route path="print/print_validation/:id" element={<StudentPrint />} />
        <Route path="print/new/:id" element={<StudentPrint />} />
        {/* end print routes */}
        {/* start moe routes */}
        <Route path="moe/moe_validation" element={<Moe />} />
        <Route path="moe/new" element={<Moe />} />
        <Route path="moe/moe_pending" element={<Moe />} />
        <Route path="moe/moe_hold" element={<Moe />} />
        <Route path="moe/aatc_review" element={<Moe />} />
        <Route path="moe/moe_validation/:id" element={<StudentMoe />} />
        <Route path="moe/new/:id" element={<StudentMoe />} />
        <Route path="moe/moe_pending/:id" element={<StudentMoe />} />
        <Route path="moe/moe_hold/:id" element={<StudentMoe />} />
        <Route path="moe/aatc_review/:id" element={<StudentMoe />} />
        <Route path="moe/moe_pending/comment" element={<MoeComment />} />
        {/* end moe routes */}
        {/* start deliver routes */}
        <Route path="deliver/deliver_onsite" element={<Deliver />} />
        <Route path="deliver/aramex" element={<Deliver />} />
        <Route path="deliver/delivery_hold" element={<Deliver />} />
        <Route path="deliver/deliver_onsite/:id" element={<StudentDeliver />} />
        <Route path="deliver/aramex/:id" element={<StudentDeliver />} />
        <Route path="deliver/delivery_hold/:id" element={<StudentDeliver />} />
        {/* end deliver routes */}
        {/* start Final routes */}
        <Route path="mohe/mohe_validation" element={<MOHE />} />
        <Route path="mohe/new" element={<MOHE />} />
        <Route path="mohe/mohe_validation/:id" element={<StudentMOHE />} />
        <Route path="mohe/new/:id" element={<StudentMOHE />} />
        <Route path="done" element={<Done />} />
        <Route path="done/:id" element={<StudentDone />} />
        {/* end done routes */}
        {/* transfer routes */}
        <Route path="transfer/submissions" element={<TransferSubmissions />} />
        {/* schools management */}
        <Route path="schools" element={<Schools />} />
        {/* enrollment letters */}
        <Route path="enrollment_letters" element={<UploadEnrollmentLetters />} />
        {/* send score submissions (approver view) */}
        <Route path="send_score_submissions" element={<SendScoreSubmissions />} />
        {/* QR codes */}
        <Route path="qrcodes" element={<QrCodes />} />
        {/* top achievers guests — open to every role */}
        <Route path="top_achievers_guests" element={<TopAchieversGuests />} />
        {/* Top Achievers event invitation — Top 50 Schools report users */}
        <Route path="top_achievers_invitation" element={<TopAchieversInvitation />} />
        <Route path="top_achievers_photos" element={<TopAchieversPhotos />} />
        {/* all top achievers (ACT1 + ACT2), filterable by date/grade/city/event location */}
        <Route path="all_top_achievers" element={<AllTopAchievers />} />
        {/* Efada (الافادة) letter — pick a student's ACT grades, download as Word */}
        <Route path="efada" element={<Efada />} />
        {/* Every generated Efada letter — admin / asmaa / abdelrhman only */}
        <Route path="efada_history" element={<EfadaHistory />} />
        {/* rush students — National ID/Name/Phone/State/Authentication Date + Delivered status */}
        <Route path="rush_students" element={<RushStudents />} />

        <Route path="reports" element={<Reports />} />
        {/* reservation slots + bookings — admin / asmaa / abdelrhman only */}
        <Route path="reservations" element={<ReservationSlots />} />
        {/* ticket QR codes link here */}
        <Route path="reservations/scan" element={<ReservationScan />} />
        <Route path="reservations/scan/:code" element={<ReservationScan />} />
      </Route>
      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/form" />} />
    </Routes>
    </Suspense>
  );
}

export default App;
